"use client";

import { Button } from "@/components/ui/button";
import DocumentUpload from "@/components/ui/document-upload";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useLocationContext } from "@/contexts/location-context";
import { buscarCep } from "@/services/flowQuote";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { saveAddress } from "../../../../services/clientAreaService";
import { ClientAddress } from "../../../../types/ClientAddress";

type AddressFormValues = {
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  municipio: string;
  cep: string;
};

type AddressFormProps = {
  cliCodigo: string;
  initial?: ClientAddress | null;
  onSaved: (addr: ClientAddress) => void;
  onCancel: () => void;
};

function CepSelect({
  value,
  onChange,
  placeholder,
  options,
  onCepValidation,
  onMunicipioSelect,
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
  options: { label: string; value: string }[];
  onCepValidation: (cep: string) => Promise<string | null>;
  onMunicipioSelect: (municipio: string) => void;
}) {
  const [inputValue, setInputValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isFocused) {
      if (value && options.some((opt) => opt.value === value)) {
        const selectedOption = options.find((opt) => opt.value === value);
        setInputValue(selectedOption?.label || value);
      } else {
        setInputValue(value);
      }
    }
  }, [value, isFocused, options]);

  const filtered = useMemo(() => {
    return options
      .filter((o) => o.label.toLowerCase().includes(inputValue.toLowerCase()))
      .slice(0, 100);
  }, [options, inputValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  const handleSelectOption = (optionValue: string) => {
    const selectedOption = options.find((o) => o.value === optionValue);
    if (selectedOption) {
      onChange("");
      setInputValue("");
      onMunicipioSelect(selectedOption.label);
    }
    setIsFocused(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();

      const matchingOption = options.find(
        (o) => o.label.toLowerCase() === inputValue.toLowerCase(),
      );

      if (!matchingOption) {
        setIsValidating(true);
        await onCepValidation(inputValue.trim());
        setIsValidating(false);
      }

      setIsFocused(false);
      inputRef.current?.blur();
    } else if (e.key === "Escape") {
      setIsFocused(false);
      inputRef.current?.blur();
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInputValue("");
    onChange("");
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full pr-16"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded-full hover:bg-muted"
            >
              <X className="h-4 w-4 text-muted-foreground" />
              <span className="sr-only">Limpar</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setIsFocused((prev) => !prev);
              if (!isFocused) {
                inputRef.current?.focus();
              }
            }}
            className="p-1 rounded-full hover:bg-muted"
          >
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
            <span className="sr-only">
              {isFocused ? "Fechar opções" : "Abrir opções"}
            </span>
          </button>
        </div>
      </div>

      {isFocused && (
        <div className="absolute z-50 w-full mt-1 bg-popover text-popover-foreground shadow-md rounded-md border max-h-[200px] overflow-auto">
          <div className="p-2">
            {filtered.length > 0 ? (
              <div className="space-y-1">
                {filtered.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelectOption(option.value)}
                    className="w-full text-left px-2 py-1 rounded hover:bg-accent flex items-center gap-2"
                  >
                    <Check className="h-4 w-4 opacity-0" />
                    {option.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-2 py-1 text-xs text-muted-foreground">
                Nenhum município encontrado. Pressione Enter para usar como CEP.
              </div>
            )}
            {inputValue.trim() && (
              <div className="pt-2 border-t mt-2">
                <Button
                  type="button"
                  variant="green"
                  size="sm"
                  onClick={async () => {
                    setIsValidating(true);
                    await onCepValidation(inputValue.trim());
                    setIsValidating(false);
                    setIsFocused(false);
                    inputRef.current?.blur();
                  }}
                  className="w-full text-sm"
                  disabled={isValidating}
                >
                  {isValidating
                    ? "Validando..."
                    : `Usar CEP: ${inputValue.trim()}`}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AddressForm({
  cliCodigo,
  initial,
  onSaved,
  onCancel,
}: AddressFormProps) {
  const { locations } = useLocationContext();
  const [cepError, setCepError] = useState<string>("");
  const [cepValidating, setCepValidating] = useState(false);
  const [cepSuccess, setCepSuccess] = useState<string>("");

  const form = useForm<AddressFormValues>({
    defaultValues: {
      endereco: initial?.cle_endereco ?? "",
      numero: initial?.cle_numero ?? "",
      complemento: initial?.cle_complemento ?? "",
      bairro: initial?.cle_bairro ?? "",
      municipio: initial?.municipio ?? "",
      cep: initial?.cle_cep ?? "",
    },
    mode: "onChange",
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name && ["endereco", "bairro", "municipio"].includes(name)) {
        setCepSuccess("");
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const validateCep = async (cep: string): Promise<string | null> => {
    setCepError("");
    setCepSuccess("");
    setCepValidating(true);

    const cepRegex = /^\d{5}-?\d{3}$/;
    if (!cepRegex.test(cep)) {
      setCepError("Digite um CEP válido no formato 00000-000");
      setCepValidating(false);
      return null;
    }

    try {
      const result = await buscarCep(cep.replace("-", ""));

      const cepInfo = result?.[0];
      const municipioInfo = result?.[1];

      if (cepInfo && municipioInfo) {
        form.setValue("endereco", cepInfo.logradouro || "");
        form.setValue("bairro", cepInfo.bairro || "");
        form.setValue("municipio", municipioInfo.mun_descricao || "");
        form.setValue("cep", cep);

        setCepError("");
        setCepSuccess(
          "Endereço preenchido automaticamente! Verifique e ajuste se necessário.",
        );
        setCepValidating(false);
        return municipioInfo.mun_codigo || null;
      } else {
        setCepError("CEP não encontrado ou informações incompletas");
        setCepValidating(false);
        return null;
      }
    } catch {
      setCepError("Erro ao validar CEP. Tente novamente.");
      setCepValidating(false);
      return null;
    }
  };

  const handleSubmit: SubmitHandler<AddressFormValues> = async (values) => {
    let munCodigo = initial?.mun_codigo ?? "";

    if (values.cep) {
      const validatedMunCodigo = await validateCep(values.cep);
      if (!validatedMunCodigo) {
        return;
      }
      munCodigo = validatedMunCodigo;
    }

    const addr: ClientAddress = {
      cle_codigo: initial?.cle_codigo ?? "",
      cle_nome: initial?.cle_nome ?? null,
      cle_endereco: values.endereco,
      cle_numero: values.numero,
      cle_complemento: values.complemento || null,
      cle_bairro: values.bairro || null,
      municipio: values.municipio,
      cle_cep: values.cep,
      cle_principal: initial?.cle_principal ?? null,
      cle_ativo: initial?.cle_ativo ?? null,
      mun_codigo: munCodigo,
      cli_codigo: cliCodigo,
    };

    setSaving(true);
    await saveAddress(cliCodigo, addr);
    setSaving(false);
    onSaved(addr);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="cep"
          render={({ field }) => (
            <FormItem>
              <label className="block text-sm mb-1">CEP:</label>
              <FormControl>
                <CepSelect
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Digite o CEP ou selecione um município"
                  options={locations}
                  onCepValidation={validateCep}
                  onMunicipioSelect={(municipio) =>
                    form.setValue("municipio", municipio)
                  }
                />
              </FormControl>
              {cepError && (
                <p className="text-red-500 text-xs mt-1">{cepError}</p>
              )}
              {cepSuccess && (
                <p className="text-green-500 text-xs mt-1">{cepSuccess}</p>
              )}
              {cepValidating && (
                <p className="text-blue-500 text-xs mt-1">Validando CEP...</p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="endereco"
          render={({ field }) => (
            <FormItem>
              <label className="block text-sm mb-1">Logradouro:</label>
              <FormControl>
                <Input placeholder="Rua / Avenida" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="numero"
          render={({ field }) => (
            <FormItem>
              <label className="block text-sm mb-1">Número:</label>
              <FormControl>
                <Input
                  placeholder="Número"
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="complemento"
          render={({ field }) => (
            <FormItem>
              <label className="block text-sm mb-1">Complemento:</label>
              <FormControl>
                <Input placeholder="Apartamento / Sala" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="bairro"
          render={({ field }) => (
            <FormItem>
              <label className="block text-sm mb-1">Bairro:</label>
              <FormControl>
                <Input placeholder="Bairro" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="municipio"
          render={({ field }) => (
            <FormItem>
              <label className="block text-sm mb-1">Cidade:</label>
              <FormControl>
                <Input placeholder="Cidade / UF" {...field} disabled />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {initial?.cle_codigo && (
          <DocumentUpload sigla="END" registro={initial.cle_codigo} />
        )}

        <div className="pt-4 flex justify-end gap-4">
          <Button
            type="button"
            variant="border"
            size="small"
            onClick={onCancel}
          >
            Cancelar
          </Button>
          <Button type="submit" size="small" disabled={saving}>
            {saving ? "Atualizando..." : "Atualizar"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
